import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const apiBaseUrl = () =>
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' &&
  (window.location.hostname === 'app.darkmattersolutions.ai' ||
    window.location.hostname === 'darkmattersolutions.ai')
    ? 'https://app.darkmattersolutions.ai'
    : 'http://129.146.2.109');

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('request'); // 'request' | 'reset'
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${apiBaseUrl()}/auth/reset-password`, { email: email.trim() });
      setMessage(res.data.message || 'If an account exists for this email, a reset code has been sent.');
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        setStep('reset');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (newPassword.length < 10) {
      setError('Password must be at least 10 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!resetToken) {
      setError('Reset code is missing. Please request a new one.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl()}/auth/reset-password/confirm`, {
        reset_token: resetToken,
        new_password: newPassword,
      });
      setMessage('Password reset successfully. You can now sign in with your new password.');
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired — request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-6"
      >
        <div className="text-center">
          <img
            src="/FullLogo_Transparent_NoBuffer.png"
            alt="Dark Matter Solutions"
            className="h-20 w-auto object-contain mx-auto mb-6"
          />
          <h1 className="text-xl font-semibold text-white">Reset password</h1>
          <p className="text-sm text-gray-400 mt-1">
            {step === 'request' && 'Enter your account email to receive a reset code.'}
            {step === 'reset' && 'Enter your new password below.'}
            {step === 'done' && 'You can now sign in.'}
          </p>
        </div>

        {step === 'request' && (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@example.com"
                  className="input-field w-full pl-10"
                  autoComplete="email"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            {message && <p className="text-sm text-green-400">{message}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send reset code'}
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleConfirmReset} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">
                New password (min 10 characters)
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  placeholder="New password"
                  className="input-field w-full pl-10 pr-10"
                  minLength={10}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm new password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="Confirm new password"
                  className="input-field w-full pl-10"
                  minLength={10}
                  autoComplete="new-password"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            {message && <p className="text-sm text-green-400">{message}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Set new password'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="space-y-4">
            <p className="text-sm text-green-400">{message}</p>
            <Link
              to="/login"
              className="block w-full py-3 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium text-center"
            >
              Back to sign in
            </Link>
          </div>
        )}

        <p className="text-center text-sm text-gray-400">
          <Link to="/login" className="text-blue-400 hover:text-blue-300">
            ← Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
